import { ConfirmDialog } from '../shared/ConfirmDialog';
import type { AccountAllowedModel, AccountModelInUseAgent } from '../../types/accountModels';

/**
 * Turning off a Model that agents use (task 1147, step 7).
 *
 * Opened when the service refuses the disallow with 409 `{ usedByAgents }`.
 * Lists those agents and offers one way through: move them to the provider's
 * default Model (each agent's Model Config keeps its temperature, output limit
 * and context budget; only the Model changes), then turn this one off. Cancel
 * leaves the Model allowed. With no other default to move to, the dialog says
 * so and offers only Close.
 */
export interface DisallowInUseState {
  model: AccountAllowedModel;
  providerName: string;
  agents: AccountModelInUseAgent[];
  target: AccountAllowedModel | null;
}

export const accountModelName = (model: Pick<AccountAllowedModel, 'displayName' | 'modelId'>) =>
  model.displayName ?? model.modelId;

interface DisallowInUseDialogProps {
  state: DisallowInUseState | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

export function DisallowInUseDialog({ state, onClose, onConfirm, isLoading }: DisallowInUseDialogProps) {
  const count = state?.agents.length ?? 0;
  const agentsLabel = `${count} ${count === 1 ? 'agent' : 'agents'}`;
  const modelName = state ? accountModelName(state.model) : '';
  const targetName = state?.target ? accountModelName(state.target) : null;

  return (
    <ConfirmDialog
      isOpen={state !== null}
      onClose={onClose}
      onConfirm={onConfirm}
      title={`${agentsLabel} ${count === 1 ? 'uses' : 'use'} ${modelName}`}
      message={
        <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300" data-testid="disallow-in-use-dialog">
          {targetName ? (
            <p>
              Turning it off moves {count === 1 ? 'it' : 'them'} to this account&apos;s {state?.providerName} default,{' '}
              <span className="font-mono font-medium text-gray-900 dark:text-white">{targetName}</span>. Each agent keeps
              its Model Config settings; only the Model changes.
            </p>
          ) : (
            <p data-testid="disallow-in-use-no-default">
              Set another default Model for {state?.providerName} first; agents on {modelName} move to it when you turn
              {' '}{modelName} off.
            </p>
          )}
          <ul
            className="divide-y divide-gray-200 rounded-md border border-gray-200 dark:divide-gray-700 dark:border-gray-700"
            data-testid="disallow-in-use-agents"
          >
            {state?.agents.map((agent) => (
              <li key={agent.id} className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                {agent.name}
              </li>
            ))}
          </ul>
        </div>
      }
      confirmLabel={targetName ? `Move to ${targetName}` : 'Move'}
      hideConfirm={!targetName}
      cancelLabel={targetName ? 'Cancel' : 'Close'}
      isLoading={isLoading}
    />
  );
}
